import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDragLayer } from "react-dnd";
import {
  ListOuterElement,
  Tree,
  type CursorProps,
  type NodeRendererProps,
  type RowRendererProps,
  type TreeApi,
} from "react-arborist";

import ChatThumbnail from "./ChatThumbnail";
import ClosedChatsHintRow, {
  CLOSED_CHATS_HINT_MARGIN_TOP,
} from "./ClosedChatsHintRow";
import { IChannel } from "../../../types/chat";
import { DEFAULT_CHAT_ROW_HEIGHT } from "../../../types/sidebar";

export const channelToId = (channel: IChannel): string => {
  return (channel.id ?? channel.name) as string;
};

/** Sentinel id/row appended to a chat list's data (never persisted, dragged,
 * or navigated to) so the "closed chats hidden" hint renders as the last row
 * of a virtualized chat list, styled like a chat item. */
export const CLOSED_CHATS_HINT_ID = "__closed-chats-hint__";
export const CLOSED_CHATS_HINT_ROW: IChannel = {
  id: CLOSED_CHATS_HINT_ID,
  name: CLOSED_CHATS_HINT_ID,
} as IChannel;
/** Taller than a regular chat row so the hint's wrapped text isn't clipped
 * (and its bottom padding stays visible) inside the fixed-height virtualized row,
 * plus the hint's top margin. */
export const CLOSED_CHATS_HINT_ROW_HEIGHT = 64 + CLOSED_CHATS_HINT_MARGIN_TOP;

/** Returns true when a visible MUI Modal/Dialog is open (ignores keepMounted hidden modals). */
export function useIsMuiModalOpen(): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const check = () => {
      const modals = document.querySelectorAll(".MuiModal-root");
      setOpen(
        Array.from(modals).some((modal) => {
          if (modal.getAttribute("aria-hidden") === "true") {
            return false;
          }

          const style = window.getComputedStyle(modal);
          return style.display !== "none" && style.visibility !== "hidden";
        }),
      );
    };

    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-hidden", "style", "class"],
    });

    return () => observer.disconnect();
  }, []);

  return open;
}

function isVisibleMuiModalOpen(): boolean {
  return Array.from(document.querySelectorAll(".MuiModal-root")).some(
    (modal) => {
      if (modal.getAttribute("aria-hidden") === "true") {
        return false;
      }

      const style = window.getComputedStyle(modal);
      return style.display !== "none" && style.visibility !== "hidden";
    },
  );
}

/** Clears react-arborist focus before row effects can steal it from an open dialog. */
export function useBlurArboristOnModalOpen(
  isModalOpen: boolean,
  ...treeRefs: Array<React.RefObject<TreeApi<IChannel> | null>>
): void {
  useLayoutEffect(() => {
    if (!isModalOpen) {
      return;
    }

    for (const ref of treeRefs) {
      ref.current?.onBlur();
    }
  }, [isModalOpen, ...treeRefs]);
}

export const useListContainerHeight = (
  watchDeps: unknown[] = [],
): {
  containerRef: (node: HTMLDivElement | null) => void;
  height: number;
  width: number;
} => {
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const nodeRef = useRef<HTMLDivElement | null>(null);

  const updateSize = useCallback(() => {
    const node = nodeRef.current;
    if (!node) {
      return;
    }

    setHeight(node.clientHeight);
    setWidth(node.clientWidth);
  }, []);

  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      nodeRef.current = node;

      if (!node) {
        setHeight(0);
        setWidth(0);
        return;
      }

      updateSize();

      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(updateSize);
        observer.observe(node);

        // Sibling accordions resize ancestor flex boxes; observe a few levels up.
        let parent = node.parentElement;
        for (let depth = 0; depth < 4 && parent; depth += 1) {
          observer.observe(parent);
          parent = parent.parentElement;
        }

        observerRef.current = observer;
      }
    },
    [updateSize],
  );

  useLayoutEffect(() => {
    updateSize();
    const raf = requestAnimationFrame(updateSize);
    return () => cancelAnimationFrame(raf);
  }, watchDeps);

  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  return { containerRef, height, width };
}

interface VirtualizedChatListProps {
  chats: IChannel[];
  chatNotifications: Set<string>;
  height: number;
  width?: number | string;
  rowHeight?: number;
  onChatDeleted: (chatName: string) => void;
  onChatPinned: (chatName: string, isPinned: boolean) => void;
  onChatSelected: (chatId: string, claimId: string | undefined) => void;
  dividerAfterChatId?: string;
}

export interface VirtualizedChatListHandle {
  scrollToTop: () => void;
}

const resolveTreeHeight = (
  chats: IChannel[],
  height: number,
  rowHeight: number,
): number => {
  if (height > 0) {
    return height;
  }

  if (chats.length > 0) {
    return chats.reduce(
      (total, chat) =>
        total +
        (channelToId(chat) === CLOSED_CHATS_HINT_ID
          ? CLOSED_CHATS_HINT_ROW_HEIGHT
          : rowHeight),
      0,
    );
  }

  return 0;
}

export const ChatListOuterElement = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ChatListOuterElement({ style, ...props }, ref) {
  return (
    <ListOuterElement
      ref={ref}
      {...props}
      style={{ ...style, overflowX: "hidden" }}
    />
  );
});

interface ChatTreeDragItem {
  id: string;
  dragIds: string[];
}

const isPointerOverElement = (
  element: HTMLElement | null,
  offset: { x: number; y: number } | null,
): boolean => {
  if (!element || !offset) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return (
    offset.x >= rect.left &&
    offset.x <= rect.right &&
    offset.y >= rect.top &&
    offset.y <= rect.bottom
  );
};

export const createChatTreeDropCursor =
  (
    allowedIds: Set<string>,
    listElementRef: React.RefObject<HTMLElement | null>,
  ) =>
  React.memo(function ScopedChatTreeDropCursor({
    top,
    left,
    indent,
  }: CursorProps) {
    const theme = useTheme();
    const { dragIds, clientOffset } = useDragLayer((monitor) => ({
      dragIds: monitor.isDragging()
        ? ((monitor.getItem() as ChatTreeDragItem | null)?.dragIds ?? [])
        : [],
      clientOffset: monitor.getClientOffset(),
    }));

    const belongsToList =
      dragIds.length > 0 && dragIds.every((id) => allowedIds.has(id));
    const isOverList = isPointerOverElement(
      listElementRef.current,
      clientOffset,
    );

    if (!belongsToList || !isOverList) {
      return null;
    }

    const color = theme.palette.custom.brandTeal;

    return (
      <div
        style={{
          position: "absolute",
          pointerEvents: "none",
          top: top - 2,
          left: left + 5,
          right: indent,
          display: "flex",
          alignItems: "center",
          zIndex: 1,
        }}
      >
        <div
          style={{
            width: 4,
            height: 4,
            boxShadow: `0 0 0 3px ${color}`,
            borderRadius: "50%",
          }}
        />
        <div
          style={{
            flex: 1,
            height: 2,
            background: color,
            borderRadius: 1,
          }}
        />
      </div>
    );
  });

export const FlatChatRow: React.FC<RowRendererProps<IChannel>> = ({
  node,
  attrs,
  innerRef,
  children,
}) => {
  const { style, ...restAttrs } = attrs;
  const child = children as React.ReactElement<{
    dragHandle?: (el: HTMLDivElement | null) => void;
  }>;
  const dragHandle = child.props.dragHandle;

  const setRowRef = useCallback(
    (el: HTMLDivElement | null) => {
      innerRef(el);
      dragHandle?.(el);
    },
    [innerRef, dragHandle],
  );

  const handleRowFocus = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    e.stopPropagation();

    if (!isVisibleMuiModalOpen()) {
      return;
    }

    const modal = document.querySelector(
      '.MuiModal-root:not([aria-hidden="true"])',
    );
    const focusTarget = modal?.querySelector<HTMLElement>(
      "input, textarea, select, [contenteditable]",
    );

    if (focusTarget) {
      focusTarget.focus();
      return;
    }

    e.currentTarget.blur();
  }, []);

  return (
    <div
      {...restAttrs}
      ref={setRowRef}
      onFocus={handleRowFocus}
      onClick={node.handleClick}
      style={{
        ...style,
        minWidth: 0,
        overflow: "hidden",
        cursor: "grab",
      }}
    >
      {React.cloneElement(child, { dragHandle: undefined })}
    </div>
  );
};

export const VirtualizedChatList = forwardRef<
  VirtualizedChatListHandle,
  VirtualizedChatListProps
>(function VirtualizedChatList(
  {
    chats,
    chatNotifications,
    height,
    width = "100%",
    rowHeight = DEFAULT_CHAT_ROW_HEIGHT,
    onChatDeleted,
    onChatPinned,
    onChatSelected,
    dividerAfterChatId,
  },
  ref,
) {
  const treeRef = useRef<TreeApi<IChannel>>(null);
  const isModalOpen = useIsMuiModalOpen();
  useBlurArboristOnModalOpen(isModalOpen, treeRef);

  useImperativeHandle(
    ref,
    () => ({
      scrollToTop: () => {
        const list = treeRef.current?.list.current;
        if (list) {
          list.scrollTo(0);
          return;
        }

        const el = treeRef.current?.listEl.current;
        if (el) {
          el.scrollTop = 0;
        }
      },
    }),
    [],
  );

  const treeHeight = resolveTreeHeight(chats, height, rowHeight);

  if (treeHeight <= 0) {
    return null;
  }

  return (
    <Tree<IChannel>
      ref={treeRef}
      data={chats}
      idAccessor={channelToId}
      width={width}
      height={treeHeight}
      rowHeight={(node) =>
        channelToId(node.data) === CLOSED_CHATS_HINT_ID
          ? CLOSED_CHATS_HINT_ROW_HEIGHT
          : rowHeight
      }
      indent={0}
      overscanCount={5}
      disableMultiSelection
      disableDrag
      disableDrop
      renderRow={FlatChatRow}
      outerElementType={ChatListOuterElement}
    >
      {(props) => (
        <ChatListNode
          {...props}
          chatNotifications={chatNotifications}
          onChatDeleted={onChatDeleted}
          onChatPinned={onChatPinned}
          onChatSelected={onChatSelected}
          showDividerBelow={channelToId(props.node.data) === dividerAfterChatId}
        />
      )}
    </Tree>
  );
});

interface ChatListNodeProps extends NodeRendererProps<IChannel> {
  chatNotifications: Set<string>;
  onChatDeleted: (chatName: string) => void;
  onChatPinned: (chatName: string, isPinned: boolean) => void;
  onChatSelected: (chatId: string, claimId: string | undefined) => void;
  showDividerBelow?: boolean;
}

const ChatListNode: React.FC<ChatListNodeProps> = ({
  node,
  style,
  chatNotifications,
  onChatDeleted,
  onChatPinned,
  onChatSelected,
  showDividerBelow,
}) => {
  const chat = node.data;

  if (channelToId(chat) === CLOSED_CHATS_HINT_ID) {
    return (
      <div style={{ ...style, overflow: "hidden", width: "100%" }}>
        <ClosedChatsHintRow />
      </div>
    );
  }

  return (
    <div style={{ ...style, overflow: "hidden", width: "100%", position: "relative" }}>
      <ChatThumbnail
        chat={chat}
        onChatDeleted={onChatDeleted}
        onChatPinned={onChatPinned}
        onChatSelected={onChatSelected}
        hasNotification={chatNotifications.has(chat.name)}
      />
      {showDividerBelow && (
        <Divider sx={{ position: "absolute", left: 0, right: 0, bottom: 0 }} />
      )}
    </div>
  );
};
